import type { ResourceDependentsPageQuery } from "src/graphql";

import { RESOURCE_DEPENDENTS_PAGE_SIZE } from "./resource-dependents-page-loader";

type ResourceDependentsPage =
  ResourceDependentsPageQuery["resourceDependentsPage"];

type ResourceDependentsPageRow = NonNullable<
  ResourceDependentsPage["rows"]
>[number];

export type UsageNode = ResourceDependentsPageRow["node"];

export type UsageRow = {
  // Unique per position in the tree, not per node. Nodes can repeat under different parents.
  key: string;
  parentKey: string | null;
  node: UsageNode;
  depth: number;
  // Exact number of resources that directly depend on this row.
  directDependentCount: number;
  isExpanded: boolean;
  // Distinguishes an unopened row from a loaded row with no rendered children.
  childrenLoaded: boolean;
  childCursor: string | null;
  isLoadingChildren: boolean;
  hasChildrenError: boolean;
};

export type UsageTreeState = {
  // The snapshot every loaded row was read from. Pages are pinned to it, so a
  // new token means a new tree rather than more rows for this one.
  snapshotToken: string | null;
  rowsByKey: ReadonlyMap<string, UsageRow>;
  // Render order of the rows, used for controlling insertion order of new rows.
  orderedKeys: readonly string[];
  rootCursor: string | null;
  isLoadingRootPage: boolean;
  hasRootPageError: boolean;
  // The snapshot has been replaced, so its cursors no longer read.
  hasExpired: boolean;
};

/**
 * Every action a request produces names the snapshot and the cursor it was
 * issued for, so the reducer can tell a result it still wants from one the tree
 * has already moved past. A result that does not match is dropped.
 */
export type UsageTreeAction =
  | {
      type: "snapshot-loaded";
      snapshotToken: string;
      page: ResourceDependentsPage;
    }
  | { type: "root-page-requested"; cursor: string }
  | {
      type: "root-page-loaded";
      snapshotToken: string;
      cursor: string;
      page: ResourceDependentsPage;
    }
  | { type: "root-page-failed"; snapshotToken: string; cursor: string }
  | { type: "children-requested"; key: string; cursor: string | null }
  | {
      type: "children-loaded";
      snapshotToken: string;
      key: string;
      cursor: string | null;
      page: ResourceDependentsPage;
    }
  | {
      type: "children-failed";
      snapshotToken: string;
      key: string;
      cursor: string | null;
    }
  | { type: "snapshot-expired"; snapshotToken: string }
  | { type: "reload-requested" }
  | { type: "row-toggled"; key: string }
  | { type: "collapsed-all" };

export function toResourceTypeCounts(
  entries: readonly { resourceType: string; count: number }[],
): ReadonlyMap<string, number> {
  return new Map(
    entries.map((entry) => [entry.resourceType, entry.count] as const),
  );
}

// Notice that we need to add the parent key to ensure uniqueness of the row key.
function rowKey(parentKey: string | null, nodeId: string): string {
  return `${parentKey ?? ""}/${nodeId}`;
}

export function emptyTreeState(): UsageTreeState {
  return {
    snapshotToken: null,
    rowsByKey: new Map(),
    orderedKeys: [],
    rootCursor: null,
    isLoadingRootPage: false,
    hasRootPageError: false,
    hasExpired: false,
  };
}

// The cursor a page carries for its continuation, or null once it is the last page.
function nextCursor(page: ResourceDependentsPage): string | null {
  return page.pageInfo.hasNextPage ? (page.pageInfo.endCursor ?? null) : null;
}

// The cursor a request for this row's next children must have. An unopened row
// has no cursor, so its first request doesn't have one.
function getChildCursor(row: UsageRow): string | null {
  return row.childrenLoaded ? row.childCursor : null;
}

function isCurrentSnapshot(
  state: UsageTreeState,
  snapshotToken: string,
): boolean {
  return !state.hasExpired && state.snapshotToken === snapshotToken;
}

/**
 * Adds a fetched page to the tree. Root rows go at the end and child rows go after
 * their parent's existing descendants. Existing rows are skipped so retries do
 * not create duplicates.
 */
function appendRows({
  state,
  parentKey,
  rows,
}: {
  state: UsageTreeState;
  parentKey: string | null;
  rows: readonly ResourceDependentsPageRow[];
}): UsageTreeState {
  const rowsByKey = new Map(state.rowsByKey);
  const parent = parentKey === null ? null : rowsByKey.get(parentKey);
  const depth = parent ? parent.depth + 1 : 0;
  const added: string[] = [];

  for (const row of rows) {
    const node = row.node;
    const key = rowKey(parentKey, node.id);
    if (rowsByKey.has(key)) {
      continue;
    }
    rowsByKey.set(key, {
      key,
      parentKey,
      node,
      depth,
      directDependentCount: row.directDependentCount,
      isExpanded: false,
      childrenLoaded: false,
      childCursor: null,
      isLoadingChildren: false,
      hasChildrenError: false,
    });
    added.push(key);
  }

  return {
    ...state,
    rowsByKey,
    orderedKeys: insertAfterSubtree(state.orderedKeys, parentKey, added),
  };
}

/**
 * Appends new child rows after the parent's existing descendants and before
 * the parent's next sibling.
 */
function insertAfterSubtree(
  orderedKeys: readonly string[],
  parentKey: string | null,
  added: readonly string[],
): string[] {
  if (added.length === 0) {
    return [...orderedKeys];
  }
  if (parentKey === null) {
    return [...orderedKeys, ...added];
  }
  const prefix = `${parentKey}/`;
  let insertAt = orderedKeys.indexOf(parentKey);
  if (insertAt === -1) {
    return [...orderedKeys, ...added];
  }
  insertAt += 1;
  while (insertAt < orderedKeys.length) {
    const key = orderedKeys[insertAt];
    if (key === undefined || !key.startsWith(prefix)) {
      break;
    }
    insertAt += 1;
  }
  return [
    ...orderedKeys.slice(0, insertAt),
    ...added,
    ...orderedKeys.slice(insertAt),
  ];
}

function patchRow(
  state: UsageTreeState,
  key: string,
  patch: Partial<UsageRow>,
): UsageTreeState {
  const row = state.rowsByKey.get(key);
  if (!row) {
    return state;
  }
  const rowsByKey = new Map(state.rowsByKey);
  rowsByKey.set(key, { ...row, ...patch });
  return { ...state, rowsByKey };
}

// The row this action addresses, while the action still matches what the row is
// waiting for.
function pendingRow(
  state: UsageTreeState,
  action: { snapshotToken: string; key: string; cursor: string | null },
): UsageRow | null {
  if (!isCurrentSnapshot(state, action.snapshotToken)) {
    return null;
  }
  const row = state.rowsByKey.get(action.key);
  if (!row || getChildCursor(row) !== action.cursor) {
    return null;
  }
  return row;
}

function stopAllLoading(state: UsageTreeState): UsageTreeState {
  const rowsByKey = new Map(state.rowsByKey);
  for (const [key, row] of rowsByKey) {
    if (row.isLoadingChildren) {
      rowsByKey.set(key, { ...row, isLoadingChildren: false });
    }
  }
  return { ...state, rowsByKey, isLoadingRootPage: false };
}

export function usageTreeReducer(
  state: UsageTreeState,
  action: UsageTreeAction,
): UsageTreeState {
  switch (action.type) {
    case "snapshot-loaded": {
      // If the snapshot token is the same, return the current state.
      if (state.snapshotToken === action.snapshotToken) {
        return state;
      }
      const seeded = appendRows({
        state: { ...emptyTreeState(), snapshotToken: action.snapshotToken },
        parentKey: null,
        rows: action.page.rows ?? [],
      });
      return { ...seeded, rootCursor: nextCursor(action.page) };
    }

    case "root-page-requested": {
      if (state.hasExpired || state.rootCursor !== action.cursor) {
        return state;
      }
      return { ...state, isLoadingRootPage: true, hasRootPageError: false };
    }

    case "root-page-loaded": {
      if (
        !isCurrentSnapshot(state, action.snapshotToken) ||
        state.rootCursor !== action.cursor
      ) {
        return state;
      }
      const merged = appendRows({
        state,
        parentKey: null,
        rows: action.page.rows ?? [],
      });
      return {
        ...merged,
        rootCursor: nextCursor(action.page),
        isLoadingRootPage: false,
        hasRootPageError: false,
      };
    }

    case "root-page-failed": {
      if (
        !isCurrentSnapshot(state, action.snapshotToken) ||
        state.rootCursor !== action.cursor
      ) {
        return state;
      }
      return { ...state, isLoadingRootPage: false, hasRootPageError: true };
    }

    case "children-requested": {
      const row = state.rowsByKey.get(action.key);
      if (state.hasExpired || !row || getChildCursor(row) !== action.cursor) {
        return state;
      }
      return patchRow(state, action.key, {
        isLoadingChildren: true,
        hasChildrenError: false,
      });
    }

    case "children-loaded": {
      if (!pendingRow(state, action)) {
        return state;
      }
      const merged = appendRows({
        state,
        parentKey: action.key,
        rows: action.page.rows ?? [],
      });
      return patchRow(merged, action.key, {
        childrenLoaded: true,
        isLoadingChildren: false,
        hasChildrenError: false,
        childCursor: nextCursor(action.page),
      });
    }

    case "children-failed": {
      if (!pendingRow(state, action)) {
        return state;
      }
      return patchRow(state, action.key, {
        isLoadingChildren: false,
        hasChildrenError: true,
      });
    }

    case "snapshot-expired": {
      if (!isCurrentSnapshot(state, action.snapshotToken)) {
        return state;
      }
      return stopAllLoading({ ...state, hasExpired: true });
    }

    case "reload-requested": {
      // Clears the expiry so the refetch can load again. A refetch that returns
      // a new snapshot rebuilds the tree, and one that returns the same
      // snapshot means the rows are still readable.
      return state.hasExpired ? { ...state, hasExpired: false } : state;
    }

    case "row-toggled": {
      const row = state.rowsByKey.get(action.key);
      if (!row) {
        return state;
      }
      return patchRow(state, action.key, { isExpanded: !row.isExpanded });
    }

    case "collapsed-all": {
      let didChange = false;
      const rowsByKey = new Map(state.rowsByKey);
      for (const [key, row] of rowsByKey) {
        if (row.isExpanded) {
          rowsByKey.set(key, { ...row, isExpanded: false });
          didChange = true;
        }
      }
      return didChange ? { ...state, rowsByKey } : state;
    }
  }
}

export function hasExpandedRows(state: UsageTreeState): boolean {
  for (const row of state.rowsByKey.values()) {
    if (row.isExpanded) {
      return true;
    }
  }
  return false;
}

export function hasExpandableRows(state: UsageTreeState): boolean {
  for (const row of state.rowsByKey.values()) {
    if (row.directDependentCount > 0) {
      return true;
    }
  }
  return false;
}

export function countLoadedChildren(
  state: UsageTreeState,
): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const row of state.rowsByKey.values()) {
    if (row.parentKey !== null) {
      counts.set(row.parentKey, (counts.get(row.parentKey) ?? 0) + 1);
    }
  }
  return counts;
}

export function pendingChildRowCount(
  row: UsageRow,
  loadedChildCount: number,
): number {
  const remaining = row.directDependentCount - loadedChildCount;
  return Math.max(1, Math.min(remaining, RESOURCE_DEPENDENTS_PAGE_SIZE));
}

// A row is hidden while any ancestor is collapsed.
function isRevealed(
  state: UsageTreeState,
  row: UsageRow,
  forcedOpenKeys: ReadonlySet<string>,
): boolean {
  let parentKey = row.parentKey;
  while (parentKey !== null) {
    const parent = state.rowsByKey.get(parentKey);
    if (!parent) {
      return false;
    }
    if (!parent.isExpanded && !forcedOpenKeys.has(parent.key)) {
      return false;
    }
    parentKey = parent.parentKey;
  }
  return true;
}

// Filters loaded rows.
// TODO: search cannot reach descendants not yet paged in.
export function filterTree(
  state: UsageTreeState,
  filter: { types: ReadonlySet<string>; search: string },
): { visibleKeys: ReadonlySet<string>; forcedOpenKeys: ReadonlySet<string> } {
  const search = filter.search.trim().toLocaleLowerCase();
  const isFiltering = filter.types.size > 0 || search.length > 0;

  const matches = (row: UsageRow): boolean => {
    if (filter.types.size > 0 && !filter.types.has(row.node.resourceType)) {
      return false;
    }
    if (search.length > 0) {
      return (row.node.name ?? "").toLocaleLowerCase().includes(search);
    }
    return true;
  };

  if (!isFiltering) {
    const visibleKeys = new Set<string>();
    for (const key of state.orderedKeys) {
      const row = state.rowsByKey.get(key);
      if (row && isRevealed(state, row, new Set())) {
        visibleKeys.add(key);
      }
    }
    return { visibleKeys, forcedOpenKeys: new Set() };
  }

  // Keep and expand a matching row's ancestors so the match remains visible.
  const visibleKeys = new Set<string>();
  const forcedOpenKeys = new Set<string>();
  for (const key of state.orderedKeys) {
    const row = state.rowsByKey.get(key);
    if (!row || !matches(row)) {
      continue;
    }
    visibleKeys.add(key);
    let parentKey = row.parentKey;
    while (parentKey) {
      const parent = state.rowsByKey.get(parentKey);
      if (!parent) {
        break;
      }
      visibleKeys.add(parent.key);
      forcedOpenKeys.add(parent.key);
      parentKey = parent.parentKey;
    }
  }
  return { visibleKeys, forcedOpenKeys };
}

// The rows to render, in order, after expansion and filtering.
export function visibleRows(
  state: UsageTreeState,
  filter: { types: ReadonlySet<string>; search: string },
): { rows: UsageRow[]; forcedOpenKeys: ReadonlySet<string> } {
  const { visibleKeys, forcedOpenKeys } = filterTree(state, filter);
  const rows: UsageRow[] = [];
  for (const key of state.orderedKeys) {
    if (!visibleKeys.has(key)) {
      continue;
    }
    const row = state.rowsByKey.get(key);
    if (row && isRevealed(state, row, forcedOpenKeys)) {
      rows.push(row);
    }
  }
  return { rows, forcedOpenKeys };
}
